import { useEffect, useMemo, useState } from 'react';
import { FiTrendingDown, FiFilter } from 'react-icons/fi';
import { IoIosRefresh } from 'react-icons/io';
import { FaPlus, FaTrash } from 'react-icons/fa6';
import ATable from '../../organisms/ATable';
import TableSkeleton from '../../atoms/TableSkeleton';
import Loading from '../../ions/Loading';
import TextEdit from '../../molecules/TextEdit';
import ExpenseFormModal from './shared/ExpenseFormModal';
import ConfirmDialog from './shared/ConfirmDialog';
import FilterPanel from './shared/FilterPanel';
import ActiveFilterChips from './shared/ActiveFilterChips';
import { buildExpenseFilterDefs } from './shared/expenseFilterTypes';
import { deriveActiveFilters } from './shared/filterTypes';
import { formatDate } from '../../../utils/collateralFormatters';
import useExpensesPage from '../../../hooks/useExpensesPage';
import useBeneficiaries from '../../../hooks/useBeneficiaries';
import type { Expense, SelectOption } from '../../../types/LegalCase';

const columns = [
    { id: 'category', title: 'Categoria', field: 'category', canSort: false, width: '220px' },
    { id: 'amount', title: 'Valor', field: 'amount', canSort: false, width: '130px', cssClass: 'text-right' },
    { id: 'paidAt', title: 'Pagamento', field: 'paidAt', canSort: false, width: '110px', cssClass: 'text-center' },
    { id: 'beneficiaryName', title: 'Beneficiário', field: 'beneficiaryName', canSort: false, width: '200px' },
    { id: 'legalCaseNumber', title: 'Processo', field: 'legalCaseNumber', canSort: false, width: '170px' },
    { id: 'actions', title: '', field: 'actions', canSort: false, width: '60px', cssClass: 'text-center' },
];

// Valor "desligado" de um filtro por id — usado ao remover um chip. Todos os filtros de
// despesa são select/choice (voltam a ''); nenhum é toggle. Fora do componente.
const clearedValue = (): string => '';

// Página top-level de Despesas (Jurídico → Despesas). Lista geral com o agregado calculado
// (total + quebra por categoria — fecha com o rodapé da planilha do jurídico), busca +
// filtros em drawer (categoria, com/sem processo, beneficiário — mesmo padrão da grid de
// Casos), cadastro por modal (caso opcional; beneficiário com "+ Novo" inline) e remoção com
// confirmação.
const ExpensePageList = () => {
    const e = useExpensesPage();
    const beneficiaries = useBeneficiaries();
    const [searchInput, setSearchInput] = useState('');
    const [showForm, setShowForm] = useState(false);
    const [pendingRemoval, setPendingRemoval] = useState<string | null>(null);
    const [initialLoading, setInitialLoading] = useState(true);
    const [filtersOpen, setFiltersOpen] = useState(false);

    useEffect(() => {
        if (!e.isLoading) setInitialLoading(false);
    }, [e.isLoading]);

    // Opções do filtro de beneficiário vêm da lista carregada em runtime; as defs do drawer
    // são montadas com elas (o request do hook usa as defs estáticas — toQueryParams não
    // depende das options).
    const beneficiaryOptions: SelectOption[] = useMemo(
        () => beneficiaries.beneficiaries.map((b) => ({ value: b.id, label: b.name })),
        [beneficiaries.beneficiaries],
    );
    const filterDefs = useMemo(
        () => buildExpenseFilterDefs(beneficiaryOptions),
        [beneficiaryOptions],
    );
    const activeFilters = deriveActiveFilters(filterDefs, e.filterValues);

    const openCreate = () => {
        e.clearErrors();
        beneficiaries.clearErrors();
        setShowForm(true);
    };

    const submitForm = async (data: Parameters<typeof e.addExpense>[0]): Promise<boolean> => {
        const ok = await e.addExpense(data);
        if (ok) setShowForm(false);
        return ok;
    };

    const confirmRemoval = async () => {
        if (!pendingRemoval) return;
        await e.removeExpense(pendingRemoval);
        setPendingRemoval(null);
    };

    return (
        <>
            {e.isLoading && <Loading />}

            <div className="flex flex-col h-screen bg-white dark:bg-slate-800 overflow-hidden">
                <div className="flex-shrink-0 border-b border-gray-200 dark:border-slate-700 p-3">
                    <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                            <FiTrendingDown className="text-blue-600" size={20} />
                            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Despesas</h1>
                            {e.summary.count > 0 && (
                                <span className="text-xs font-medium px-2 py-0.5 rounded bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300">
                                    Total: {e.summary.totalFormatted}
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-56">
                                <TextEdit
                                    label="Buscar descrição..."
                                    value={searchInput}
                                    onChange={(ev: any) => setSearchInput(ev.target.value)}
                                    onKeyDown={(ev: any) => { if (ev.key === 'Enter') e.setSearch(searchInput); }}
                                />
                            </div>
                            <button onClick={() => setFiltersOpen(true)}
                                className="relative flex items-center gap-2 px-3 py-2 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 text-sm font-medium"
                                title="Filtros">
                                <FiFilter size={14} /> Filtros
                                {activeFilters.length > 0 && (
                                    <span className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold bg-blue-600 text-white">
                                        {activeFilters.length}
                                    </span>
                                )}
                            </button>
                            <button onClick={e.refresh}
                                className="p-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                                title="Atualizar lista">
                                <IoIosRefresh size={18} />
                            </button>
                            <button onClick={openCreate}
                                className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">
                                <FaPlus size={12} /> Novo
                            </button>
                        </div>
                    </div>
                </div>

                <ActiveFilterChips
                    filters={activeFilters}
                    onRemove={(id) => e.setFilterValue(id, clearedValue())}
                    onClearAll={e.clearFilters}
                />

                <div className="flex-1 overflow-auto [&_table]:table-fixed [&_table]:w-full">
                    {initialLoading ? (
                        <TableSkeleton rows={12} />
                    ) : (
                        <ATable headers={columns}>
                            {e.expenses.length === 0 ? (
                                <tr><td colSpan={columns.length} className="py-8 text-center text-gray-500 dark:text-gray-400">Nenhuma despesa cadastrada</td></tr>
                            ) : e.expenses.map((row: Expense, index: number) => (
                                <tr key={row.id}
                                    className={`group ${index % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-900'}`}>
                                    <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm dark:text-gray-300">
                                        <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300">
                                            {row.categoryFormatted}
                                        </span>
                                    </td>
                                    <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm text-right font-medium dark:text-white">{row.amountFormatted || '—'}</td>
                                    <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm text-center text-gray-500 dark:text-gray-400">{row.paidAt ? formatDate(row.paidAt) : '—'}</td>
                                    <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm dark:text-gray-300 truncate">{row.beneficiaryName || '—'}</td>
                                    <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm font-mono text-gray-500 dark:text-gray-400 truncate">{row.legalCaseNumber || '—'}</td>
                                    <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-center">
                                        <button type="button" onClick={() => setPendingRemoval(row.id)} title="Remover"
                                            className="p-1.5 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900 rounded-lg">
                                            <FaTrash size={11} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </ATable>
                    )}
                </div>
            </div>

            <FilterPanel
                open={filtersOpen}
                defs={filterDefs}
                values={e.filterValues}
                onChange={e.setFilterValue}
                onClearAll={e.clearFilters}
                onClose={() => setFiltersOpen(false)}
            />

            {showForm && (
                <ExpenseFormModal
                    categoryOptions={e.categoryOptions}
                    beneficiaries={beneficiaries.beneficiaries}
                    beneficiaryTypeOptions={e.beneficiaryTypeOptions}
                    onCreateBeneficiary={beneficiaries.addBeneficiary}
                    beneficiaryErrorFields={beneficiaries.errorFields}
                    isBeneficiarySaving={beneficiaries.isSaving}
                    errorFields={e.errorFields}
                    isSaving={e.isSaving}
                    onSubmit={submitForm}
                    onCancel={() => setShowForm(false)}
                />
            )}

            <ConfirmDialog
                open={pendingRemoval !== null}
                title="Remover despesa"
                message="Remover esta despesa? Esta ação não pode ser desfeita."
                isBusy={e.isSaving}
                onConfirm={confirmRemoval}
                onCancel={() => setPendingRemoval(null)}
            />
        </>
    );
};

export default ExpensePageList;
